import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, new_password) => api.post('/auth/reset-password', { token, new_password }),
  verifyResetToken: (token) => api.get(`/auth/verify-reset-token?token=${token}`),
};

// Plans API
export const plansApi = {
  getPlans: () => api.get('/plans'),
  getMyUsage: () => api.get('/my-usage'),
  updateEmailPreferences: (disabled) => api.put('/my-usage/email-preferences', { email_notifications_disabled: disabled }),
};

// Admin API
export const adminApi = {
  getStats: () => api.get('/admin/stats'),
  getUsers: (params) => api.get('/admin/users', { params }),
  getUserDetails: (userId) => api.get(`/admin/users/${userId}`),
  updateUserPlan: (userId, plan) => api.put(`/admin/users/${userId}/plan`, { plan }),
  toggleUserActive: (userId) => api.put(`/admin/users/${userId}/toggle-active`),
  toggleUserAdmin: (userId) => api.put(`/admin/users/${userId}/toggle-admin`),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  getErrors: () => api.get('/admin/errors'),
  clearErrors: () => api.delete('/admin/errors'),
  getSchedulerStatus: () => api.get('/admin/scheduler-status'),
  triggerDailyPrompts: () => api.post('/admin/trigger-daily-prompts'),
  triggerWeeklySummaries: () => api.post('/admin/trigger-weekly-summaries'),
  // Promo codes
  getPromoCodes: () => api.get('/admin/promo-codes'),
  createPromoCode: (data) => api.post('/admin/promo-codes', data),
  deletePromoCode: (code) => api.delete(`/admin/promo-codes/${code}`),
  togglePromoCode: (code) => api.put(`/admin/promo-codes/${code}/toggle`),
  updatePromoCode: (code, data) => api.put(`/admin/promo-codes/${code}/update`, null, { params: data }),
  getPromoRedemptions: (code) => api.get(`/admin/promo-codes/${code}/redemptions`),
  // Beta feedback
  getFeedback: (skip = 0, limit = 50) => api.get(`/admin/feedback?skip=${skip}&limit=${limit}`),
  // Engagement trends
  getEngagementTrends: (days = 14) => api.get(`/admin/engagement-trends?days=${days}`),
  // Seed learning modules
  seedLearningModules: () => api.post('/admin/seed-learning-modules'),
  // Force reseed modules (delete and recreate)
  forceReseedModules: () => api.post('/admin/force-reseed-modules'),
  // Debug modules
  debugModules: () => api.get('/admin/debug-modules'),
  // Beta management
  bulkInviteBeta: (emails, promoCode) => api.post('/admin/beta/bulk-invite', null, { params: { emails: Array.isArray(emails) ? emails.join(',') : emails, plan: 'enterprise', duration_days: 30 } }),
  getBetaTesters: () => api.get('/admin/beta/testers'),
  getBetaFeedback: () => api.get('/admin/beta/feedback'),
  // Client Management
  createClient: (data) => api.post('/admin/clients/create', data),
  getClientFullProfile: (userId) => api.get(`/admin/clients/${userId}/full-profile`),
  updateClientStrategic: (userId, data) => api.put(`/admin/clients/${userId}/strategic`, data),
  addClientPriority: (userId, data) => api.post(`/admin/clients/${userId}/priorities`, data),
  deleteClientPriority: (userId, priorityId) => api.delete(`/admin/clients/${userId}/priorities/${priorityId}`),
  addClientGoal: (userId, data) => api.post(`/admin/clients/${userId}/goals`, data),
  updateClientGoal: (userId, goalId, data) => api.put(`/admin/clients/${userId}/goals/${goalId}`, data),
  deleteClientGoal: (userId, goalId) => api.delete(`/admin/clients/${userId}/goals/${goalId}`),
  addClientTask: (userId, data) => api.post(`/admin/clients/${userId}/tasks`, data),
  deleteClientTask: (userId, taskId) => api.delete(`/admin/clients/${userId}/tasks/${taskId}`),
  resetClientPassword: (userId, newPassword) => api.put(`/admin/clients/${userId}/reset-password`, null, { params: { new_password: newPassword } }),
  updateClientProfile: (userId, data) => api.put(`/admin/clients/${userId}/profile`, null, { params: data }),
};

// Beta Feedback API (user-facing)
export const feedbackApi = {
  submitBetaFeedback: (data) => api.post('/feedback/beta', data),
  getMyFeedback: () => api.get('/feedback/beta/mine'),
  submit: (data) => api.post('/feedback', data),
};

// Promo Codes API (public/user)
export const promoApi = {
  validate: (code) => api.get(`/promo-codes/validate/${code}`),
  redeem: (code) => api.post('/promo-codes/redeem', { code }),
};

// Modules API
export const modulesApi = {
  getAll: () => api.get('/modules'),
  getOne: (id) => api.get(`/modules/${id}`),
  updateProgress: (moduleId, progressPercent) => 
    api.post(`/modules/${moduleId}/progress?progress_percent=${progressPercent}`),
  getMyProgress: () => api.get('/modules/progress/me'),
  getAudioUrl: (moduleId) => `${API_BASE}/modules/${moduleId}/audio`,
};

// Prompts API
export const promptsApi = {
  getToday: () => api.get('/prompts/today'),
  getAll: () => api.get('/prompts'),
  respond: (promptId, responseText) => 
    api.post(`/prompts/${promptId}/respond?response_text=${encodeURIComponent(responseText)}`),
  getMyResponses: () => api.get('/prompts/responses/me'),
  createTaskFromResponse: (responseId, taskTitle = null) => 
    api.post(`/prompts/response/${responseId}/create-task${taskTitle ? `?task_title=${encodeURIComponent(taskTitle)}` : ''}`),
};

// Vision Story API
export const visionStoryApi = {
  generate: (format = 'story', voice = 'nova') => 
    api.post('/vision-story/generate', { format, voice }),
  get: (storyId) => api.get(`/vision-story/${storyId}`),
  getAll: () => api.get('/vision-story'),
  delete: (storyId) => api.delete(`/vision-story/${storyId}`),
};

// Goals API
export const goalsApi = {
  create: (data) => api.post('/goals', data),
  getAll: () => api.get('/goals'),
  getOne: (id) => api.get(`/goals/${id}`),
  update: (id, data) => api.put(`/goals/${id}`, data),
  updateStatus: (id, status) => api.put(`/goals/${id}/status?status=${status}`),
  updateKeyResult: (goalId, krId, currentValue) => 
    api.put(`/goals/${goalId}/key-results/${krId}`, { current_value: currentValue }),
  delete: (id) => api.delete(`/goals/${id}`),
  fixDates: () => api.post('/goals/fix-dates'),
};

// Tasks API
export const tasksApi = {
  create: (data) => api.post('/tasks', data),
  getAll: (status) => api.get('/tasks', { params: status ? { status } : {} }),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
  // Daily task completion
  completeToday: (id) => api.post(`/tasks/${id}/complete-today`),
  uncompleteToday: (id) => api.delete(`/tasks/${id}/complete-today`),
  getDailyStats: (id) => api.get(`/tasks/${id}/daily-stats`),
  getDailySummary: () => api.get('/tasks/daily-summary'),
};

// Alerts API
export const alertsApi = {
  getDueToday: () => api.get('/due-today'),
};

// Chat API
export const chatApi = {
  send: (message, sessionId) => api.post('/chat', { message, session_id: sessionId }),
  getHistory: (sessionId) => api.get(`/chat/history/${sessionId}`),
  getSessions: () => api.get('/chat/sessions'),
  getStrategicSuggestion: (type, context, currentValue) => api.post('/strategic-ai/suggest', { type, context, current_value: currentValue }),
};

// Analytics API
export const analyticsApi = {
  get: () => api.get('/analytics'),
};

// Next Actions API
export const nextActionsApi = {
  get: () => api.get('/next-actions'),
};

// Weekly Summary API
export const weeklySummaryApi = {
  get: () => api.get('/weekly-summary'),
  refresh: () => api.post('/weekly-summary/refresh'),
};

// Scorecard API
export const scorecardApi = {
  get: () => api.get('/scorecard'),
};

// Teams API
export const teamsApi = {
  create: (data) => api.post('/teams', data),
  getAll: () => api.get('/teams'),
  getOne: (id) => api.get(`/teams/${id}`),
  update: (id, data) => api.put(`/teams/${id}`, data),
  delete: (id) => api.delete(`/teams/${id}`),
  invite: (teamId, email) => api.post(`/teams/${teamId}/invite`, { email }),
  getInvites: (teamId) => api.get(`/teams/${teamId}/invites`),
  leave: (teamId) => api.post(`/teams/${teamId}/leave`),
  updateMemberRole: (teamId, memberId, role) => api.put(`/teams/${teamId}/members/${memberId}/role?role=${role}`),
  removeMember: (teamId, memberId) => api.delete(`/teams/${teamId}/members/${memberId}`),
  getActivity: (teamId) => api.get(`/teams/${teamId}/activity`),
  getStats: (teamId) => api.get(`/teams/${teamId}/stats`),
  // Team Goals
  createGoal: (teamId, data) => api.post(`/teams/${teamId}/goals`, data),
  getGoals: (teamId) => api.get(`/teams/${teamId}/goals`),
  updateGoal: (teamId, goalId, data) => api.put(`/teams/${teamId}/goals/${goalId}`, data),
  updateGoalKeyResult: (teamId, goalId, krId, currentValue) => 
    api.put(`/teams/${teamId}/goals/${goalId}/key-results/${krId}`, { current_value: currentValue }),
  updateGoalStatus: (teamId, goalId, status) => api.put(`/teams/${teamId}/goals/${goalId}/status?status=${status}`),
  deleteGoal: (teamId, goalId) => api.delete(`/teams/${teamId}/goals/${goalId}`),
};

// Invites API
export const invitesApi = {
  getPending: () => api.get('/invites/pending'),
  accept: (inviteId) => api.post(`/invites/${inviteId}/accept`),
  decline: (inviteId) => api.post(`/invites/${inviteId}/decline`),
};

// Seed API
export const seedApi = {
  seed: () => api.post('/seed'),
};

// Subscription API
export const subscriptionApi = {
  createCheckout: (packageId, billingPeriod = 'monthly') => api.post('/subscribe', { 
    package_id: packageId, 
    billing_period: billingPeriod,
    origin_url: window.location.origin 
  }),
  getStatus: (sessionId) => api.get(`/subscribe/status/${sessionId}`),
  getHistory: () => api.get('/subscription/history'),
};

// Google Calendar API
export const calendarApi = {
  connect: () => api.get('/oauth/calendar/connect'),
  disconnect: () => api.delete('/oauth/calendar/disconnect'),
  getStatus: () => api.get('/calendar/status'),
  updateSettings: (settings) => api.put('/calendar/settings', settings),
  syncAll: () => api.post('/calendar/sync-all'),
  getEvents: () => api.get('/calendar/events'),
  importEvent: (eventId, importAs) => api.post('/calendar/import', { event_id: eventId, import_as: importAs }),
};

// Data Export/Import API
export const dataApi = {
  exportMyData: () => api.get('/export/my-data'),
  importMyData: (data) => api.post('/import/my-data', data),
  adminExportUser: (userId) => api.get(`/admin/export/user/${userId}`),
  adminExportAll: () => api.get('/admin/export/all'),
};

// Strategic Objectives API (Enterprise)
export const strategicObjectivesApi = {
  getObjectives: () => api.get('/strategic-objectives'),
  updateVision: (data) => api.put('/strategic-objectives/vision', data),
  updateMission: (data) => api.put('/strategic-objectives/mission', data),
  getPriorities: () => api.get('/strategic-objectives/priorities'),
  createPriority: (data) => api.post('/strategic-objectives/priorities', data),
  updatePriority: (id, data) => api.put(`/strategic-objectives/priorities/${id}`, data),
  deletePriority: (id) => api.delete(`/strategic-objectives/priorities/${id}`),
  reorderPriorities: (priorityIds) => api.put('/strategic-objectives/priorities/reorder', { priority_ids: priorityIds }),
};

export default api;
